<template>
  <div class="admin-users">
    <b-row>
      <b-col cols="12">
        <b-card no-body>

          <div class="m-2">
            <b-row class="align-items-end">

              <b-col
                cols="12"
              >
                <b-form
                  @submit.prevent="applyFilters"
                  @reset.prevent="resetFilters"
                >
                  <b-row>
                    <b-col
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <b-form-group :label="$t('admin.users.filters.type')">
                        <v-select
                          v-model="filters.type"
                          :options="userTypeOptions"
                          :clearable="true"
                          :reduce="option => option.value"
                          label="text"
                          :placeholder="$t('admin.users.filters.typePlaceholder')"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <b-form-group :label="$t('admin.users.filters.firstName')">
                        <b-form-input
                          v-model="filters.firstName"
                          :placeholder="$t('admin.users.filters.firstNamePlaceholder')"
                          autocomplete="off"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <b-form-group :label="$t('admin.users.filters.lastName')">
                        <b-form-input
                          v-model="filters.lastName"
                          :placeholder="$t('admin.users.filters.lastNamePlaceholder')"
                          autocomplete="off"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <b-form-group :label="$t('admin.users.filters.email')">
                        <b-form-input
                          v-model="filters.email"
                          :placeholder="$t('admin.users.filters.emailPlaceholder')"
                          autocomplete="off"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <b-form-group :label="$t('admin.users.filters.phoneNumber')">
                        <b-form-input
                          v-model="filters.phoneNumber"
                          :placeholder="$t('admin.users.filters.phoneNumberPlaceholder')"
                          autocomplete="off"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col
                      cols="12"
                      sm="6"
                      class="d-flex align-items-center justify-content-start"
                    >
                      <label class="mb-0">{{ $t('shared.show') }}</label>
                      <v-select
                        v-model="perPage"
                        :options="perPageOptions"
                        :clearable="false"
                        class="per-page-selector d-inline-block mx-50"
                        @input="onPerPageChange"
                      />
                      <label class="mb-0">{{ $t('shared.entries') }}</label>
                    </b-col>

                    <b-col
                      cols="12"
                      sm="6"
                      class="d-flex justify-content-end mt-1"
                    >
                      <b-button
                        variant="outline-secondary"
                        class="mr-1"
                        @click="resetFilters"
                      >
                        {{ $t('admin.users.actions.reset') }}
                      </b-button>
                      <b-button
                        type="submit"
                        variant="primary"
                      >
                        {{ $t('admin.users.actions.apply') }}
                      </b-button>
                    </b-col>
                  </b-row>
                </b-form>
              </b-col>
            </b-row>
          </div>

          <rc-overlay :show="loading">
            <b-table
              primary-key="id"
              responsive
              hover
              :items="users"
              :fields="tableColumns"
              show-empty
              :empty-text="loading ? '' : $t('shared.no-data')"
              class="mb-0"
            >
              <template #cell(type)="{ item }">
                {{ $t('admin.users.filters.typeOptions.' + item.type) }}
              </template>

              <template #cell(verified)="{ item }">
                <b-badge :variant="item.verified ? 'success' : 'secondary'">
                  {{ item.verified ? $t('shared.yes') : $t('shared.no') }}
                </b-badge>
              </template>

              <template #cell(active)="{ item }">
                <b-badge :variant="item.active ? 'success' : 'secondary'">
                  {{ item.active ? $t('shared.yes') : $t('shared.no') }}
                </b-badge>
              </template>

              <template #cell(actions)="{ item }">
                <b-button
                  variant="flat-primary"
                  size="sm"
                  :disabled="impersonating || isImpersonating || (currentUser && currentUser.id === item.id)"
                  @click="impersonate(item)"
                >
                  <feather-icon
                    icon="UserCheckIcon"
                    class="mr-50"
                  />
                  {{ $t('admin.users.actions.impersonate') }}
                </b-button>
              </template>
            </b-table>
          </rc-overlay>

          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                md="6"
                class="d-flex align-items-center justify-content-center justify-content-md-start mb-1 mb-md-0"
              >
                <span class="text-muted">{{ $t('shared.pagination-summary', dataMeta) }}</span>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="d-flex align-items-center justify-content-center justify-content-md-end"
              >
                <b-pagination
                  v-if="totalItems > perPage"
                  v-model="currentPage"
                  :total-rows="totalItems"
                  :per-page="perPage"
                  first-number
                  last-number
                  class="mb-0"
                  prev-class="prev-item"
                  next-class="next-item"
                  @change="onPageChange"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BBadge,
  BPagination,
  BRow,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import userTypes from '@/auth/type'
import RcOverlay from '@/layouts/components/rc/RcOverlay'
import adminRequests from '@/http/requests/admin'
import { parseRequestError } from '@/helpers/helpers'

export default {
  name: 'AdminUsers',
  components: {
    RcOverlay,
    vSelect,
    BRow,
    BCol,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BBadge,
  },
  data() {
    return {
      filters: {
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        type: null,
      },
      perPage: 20,
      perPageOptions: [10, 20, 50, 100],
      currentPage: 1,
      totalItems: 0,
      users: [],
      loading: false,
      impersonating: false,
      tableColumns: this.getTableColumns(),
    }
  },
  computed: {
    dataMeta() {
      const from = this.totalItems === 0 ? 0 : (this.perPage * (this.currentPage - 1)) + 1
      const to = (this.perPage * (this.currentPage - 1)) + this.users.length
      return { from, to, of: this.totalItems }
    },
    isImpersonating() {
      return this.$store.getters['auth/isImpersonating']
    },
    currentUser() {
      return this.$store.getters['auth/getUser']
    },
    userTypeOptions() {
      const { locale } = this.$i18n

      return Object.values(userTypes).map(type => ({
        value: type,
        text: this.$t(`admin.users.filters.typeOptions.${type}`, { type, locale }),
      }))
    },
  },
  watch: {
    '$i18n.locale': function onLocaleChange() {
      this.tableColumns = this.getTableColumns()
    },
  },
  created() {
    this.fetchUsers()
  },
  methods: {
    getTableColumns() {
      return [
        { key: 'id', label: this.$t('admin.users.table.id'), sortable: false },
        { key: 'type', label: this.$t('admin.users.table.type'), sortable: false },
        { key: 'verified', label: this.$t('admin.users.table.verified'), sortable: false },
        { key: 'active', label: this.$t('admin.users.table.active'), sortable: false },
        { key: 'firstName', label: this.$t('admin.users.table.firstName'), sortable: false },
        { key: 'lastName', label: this.$t('admin.users.table.lastName'), sortable: false },
        { key: 'email', label: this.$t('admin.users.table.email'), sortable: false },
        { key: 'phoneNumber', label: this.$t('admin.users.table.phoneNumber'), sortable: false },
        {
          key: 'actions', label: this.$t('admin.users.table.actions'), sortable: false, class: 'text-right', thClass: 'text-right', tdClass: 'text-right',
        },
      ]
    },
    buildQueryParams() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
      }

      Object.entries(this.filters).forEach(([key, value]) => {
        if (typeof value === 'string') {
          const trimmed = value.trim()
          if (trimmed.length) {
            params[key] = trimmed
          }
        } else if (value !== null && value !== undefined && value !== '') {
          params[key] = value
        }
      })

      return params
    },
    fetchUsers() {
      this.loading = true
      adminRequests.listUsers(this.buildQueryParams())
        .then(response => {
          const { data } = response
          this.users = data.items || []
          this.totalItems = typeof data.filtered !== 'undefined' ? data.filtered : (data.total || this.users.length)
        })
        .catch(error => {
          const errPayload = error?.response?.data || error
          parseRequestError(errPayload)
          window.toast.notify.danger(this.$t('admin.users.notifications.fetchError'))
          this.users = []
          this.totalItems = 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    applyFilters() {
      this.currentPage = 1
      this.fetchUsers()
    },
    resetFilters() {
      this.filters = {
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        type: null,
      }
      this.applyFilters()
    },
    onPerPageChange(value) {
      this.perPage = Number(value)
      this.currentPage = 1
      this.fetchUsers()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchUsers()
    },
    impersonate(user) {
      if (!user || !user.id) {
        return
      }

      if (this.currentUser && this.currentUser.id === user.id) {
        return
      }

      this.impersonating = true
      this.$store.dispatch('auth/impersonateUser', user.id)
        .then(() => {
          window.toast.notify.success(
            this.$t('admin.users.notifications.impersonationSuccessTitle'),
            this.$t('admin.users.notifications.impersonationSuccessSubtitle', { email: user.email }),
          )
          this.reloadView()
        })
        .catch(error => {
          const errPayload = error?.response?.data || error
          parseRequestError(errPayload)
        })
        .finally(() => {
          this.impersonating = false
        })
    },
    reloadView() {
      const performReload = () => {
        if (typeof window !== 'undefined') {
          window.location.reload()
        }
      }

      this.$router.replace({ name: 'dashboard-redirect' })
        .then(performReload)
        .catch(() => {
          performReload()
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>
